import { Component, OnDestroy, OnInit } from '@angular/core';
import { I18NService } from '@ebw-monorepo/i18n-translate';
import { EbwModalService } from '@ebw-monorepo/modal';
import { MapBrowserEvent } from 'ol';
import { transform } from 'ol/proj';
import { Subscription } from 'rxjs';
import { IHttpMapGet } from '../../../interfaces/httpReq.interface';
import { LayerStylesService } from '../../../services/layer-styles.service';
import { MapRequestService } from '../../../services/map-request.service';
import { MapService } from '../../../services/map.service';
import { API_URLS, ApiUrlService } from 'libs/ng-components/utils/src/lib/services/api-url.service';
import { LoginService } from '@ebw-monorepo/login';

type LineSegment = {
  coords: [number, number];
  start: string;
  end: string;
};

@Component({
  selector: 'ebw-monorepo-tracing',
  templateUrl: './tracing.component.html',
  styleUrls: ['./tracing.component.scss']
})
export class TracingComponent implements OnInit, OnDestroy {
  mapClickSub: Subscription;
  clickSource: 'start' | 'end' | '' = '';
  isTraceOut = false;

  startPoint = '';
  startJSONFeature = null;
  isStartPointSelected = false;

  endPoint = '';
  endJSONFeature = null;
  isEndPointSelected = false;

  distance = 0;

  tracingStartPointStyle: any;
  tracingEndPointStyle: any;
  tracingLineStyle: any;

  private static readonly CLICK_OWNER_ID = 'tracing';

  constructor(
    private mapService: MapService,
    private ebwModalService: EbwModalService,
    private i18nSrv: I18NService,
    private mapRequestService: MapRequestService,
    private layerStyleService: LayerStylesService,
    private apiUrlService: ApiUrlService
  ) {}

  ngOnInit(): void {
    this.tracingStartPointStyle = this.layerStyleService.buildFeatureStyleFromId('TracingStartPoint');
    this.tracingEndPointStyle = this.layerStyleService.buildFeatureStyleFromId('TracingEndPoint');
    this.tracingLineStyle = this.layerStyleService.buildFeatureStyleFromId('TracingLine');
  }

  ngOnDestroy(): void {
    this.onReset();
    this.disableCustomClick();
  }

  onEventToggle(event: Event) {
    this.clearTrace();
    if (this.isTraceOut) {
      this.resetPoint('end');
    } else {
      this.distance = 0;
    }
  }

  onReset() {
    this.isTraceOut = false;
    this.resetPoint('start');
    this.resetPoint('end');
    this.clearTrace();
    this.distance = 0;
    this.disableCustomClick();
  }

  resetPoint(type: 'start' | 'end') {
    if (type === 'start') {
      this.startPoint = '';
      this.isStartPointSelected = false;
      this.clearMapFeature('start');
    } else {
      this.endPoint = '';
      this.isEndPointSelected = false;
      this.clearMapFeature('end');
    }
  }

  toggleCustomClick(type: 'start' | 'end') {
    this.disableCustomClick();
    this.selectPoint(type);
  }

  selectPoint(type: 'start' | 'end') {
    const isSelected = type === 'start' ? this.isStartPointSelected : this.isEndPointSelected;

    if (isSelected) {
      this.ebwModalService
        .openConfirmModal({
          title: 'Info',
          content: `Per inserire un nuovo punto il precedente verrà cancellato. Procedere?`
        })
        .afterClosed()
        .subscribe((result) => {
          if (result) {
            this.resetPoint(type);
            this.enableCustomClick(type);
          }
        });
    } else {
      this.resetPoint(type);
      this.enableCustomClick(type);
    }
  }

  enableCustomClick(type: 'start' | 'end') {
    if (this.clickSource) {
      this.disableCustomClick();
      return;
    }

    if (this.mapClickSub) {
      this.ebwModalService.openInfoModal({
        title: 'Info',
        content: 'Strumento di selezione già aperto'
      });
      return;
    }

    // il click di selezione è "strong": se c'è già uno strumento aperto il registro mostra l'errore. Va acquisito prima del flag
    if (!this.mapService.acquireMapClick({ id: TracingComponent.CLICK_OWNER_ID, level: 'strong' })) {
      return;
    }

    this.clickSource = type;
    this.mapService.customMapClickEnable$.next(true);

    this.mapClickSub = this.mapService.mapClicked$.subscribe((event) => {
      this.disableCustomClick();
      this.handleMapClick(event, type);
    });
  }

  disableCustomClick() {
    if (this.mapClickSub) {
      this.mapClickSub.unsubscribe();
      this.mapClickSub = null;
      this.clickSource = '';
      this.mapService.customMapClickEnable$.next(false);
      // il flag va spento prima di rilasciare il registro (un passive in attesa lo riaccende)
      this.mapService.releaseMapClick(TracingComponent.CLICK_OWNER_ID);
    }
  }

  handleMapClick(event: MapBrowserEvent<any>, type: 'start' | 'end') {
    if (event.coordinate?.length) {
      const [x, y] = event.coordinate;
      this.getUrnFromCoordinates(y, x, type);
    }
  }

  getUrnFromCoordinates(lat: number, lng: number, type: 'start' | 'end') {
    const [lngTrasformed, latTransformed] = transform([lng, lat], MapService.EPSG_3857_KEY, MapService.EPSG_4326_KEY);
    const HIT_RADIUS = 5;

    const req: IHttpMapGet = {
      url: `${this.apiUrlService.createUrl(
        API_URLS.MAP.CORE.CHECK_GRANT
      )}?request_type=select&filter=PNI&hit_radius=${HIT_RADIUS}&lat=${latTransformed}&lng=${lngTrasformed}`,
      user_id: sessionStorage.getItem(LoginService.USER_ID_KEY)
    };

    this.mapRequestService.getMapRequest(req).subscribe({
      next: (resp: any) => {
        // filter only Point feature
        const features = resp?.result?.features?.filter((el) => el.geometry?.type == 'Point');
        if (features.length > 0) {
          if (type === 'start') {
            this.startJSONFeature = features[0];
            const transformedCoords = transform(
              this.startJSONFeature.geometry.coordinates,
              MapService.EPSG_4326_KEY,
              MapService.EPSG_3857_KEY
            );
            this.startJSONFeature.geometry.coordinates = transformedCoords;
            this.startPoint = this.startJSONFeature.id;
            this.isStartPointSelected = true;
            this.drawMapFeature('start');
          } else {
            // filter feature with same dataset of start point
            let startPointDataset = this.startPoint.split('V')[2].split('Z')[1];
            let filteredFeatures = features.filter((el) => {
              let featureDataset = el.id.split('V')[2].split('Z')[1];
              return startPointDataset == featureDataset;
            });

            if (filteredFeatures.length == 0) {
              return this.showErrorModal(
                `Nessun elemento trovato appartenente alla stessa rete dell'elemento di partenza`
              );
            }
            this.endJSONFeature = filteredFeatures[0];
            const transformedCoords = transform(
              this.endJSONFeature.geometry.coordinates,
              MapService.EPSG_4326_KEY,
              MapService.EPSG_3857_KEY
            );
            this.endJSONFeature.geometry.coordinates = transformedCoords;
            this.endPoint = this.endJSONFeature.id;
            this.isEndPointSelected = true;
            this.drawMapFeature('end');
          }
        } else {
          this.showErrorModal('Non sono presenti elementi puntuali');
        }
      },
      error: () => this.showErrorModal('Errore recupero elemento')
    });
  }

  showErrorModal(message?: string) {
    this.ebwModalService.openErrorModal({
      title: this.i18nSrv.getInstantValue('common.error'),
      content: message ?? this.i18nSrv.getInstantValue('common.data_retrieving_error')
    });
  }

  submitSearch() {
    if (!this.isStartPointSelected) {
      this.ebwModalService.openErrorModal({
        title: 'Errore',
        content: 'Seleziona un punto di partenza sulla mappa.'
      });
      return;
    }

    if (this.isTraceOut) {
      if (!this.distance || this.distance < 1) {
        this.ebwModalService.openErrorModal({
          title: 'Errore',
          content: 'Inserisci una distanza valida (minimo 1 metro).'
        });
        return;
      }
      this.callTraceOutAPI(this.startPoint, this.distance);
    } else {
      if (!this.isEndPointSelected) {
        this.ebwModalService.openErrorModal({
          title: 'Errore',
          content: 'Seleziona un punto di arrivo sulla mappa.'
        });
        return;
      }
      this.callShortestPathAPI(this.startPoint, this.endPoint);
    }
  }

  callTraceOutAPI(start: string, distance: number) {
    this.clearTrace();

    console.log(`Calling Trace Out API with start: ${start}, distance: ${distance}`);
    const req: IHttpMapGet = {
      url: `${this.apiUrlService.createUrl(API_URLS.MAP.TRACING.TRACE_OUT)}?urn_start=${start}&distance=${distance}`,
      user_id: sessionStorage.getItem(LoginService.USER_ID_KEY)
    };

    this.mapRequestService.getMapRequest(req).subscribe({
      next: (resp: any) => {
        this.drawTrace(resp.result.features, false);
      },
      error: (err) => {
        console.log('TRACE OUT ERROR', err);
        this.showErrorModal('Impossibile trovare un percorso per questi valori');
      }
    });
  }

  callShortestPathAPI(start: string, end: string) {
    this.clearTrace();

    console.log(`Calling Shortest Path API with start: ${start}, end: ${end}`);
    const req: IHttpMapGet = {
      url: `${this.apiUrlService.createUrl(API_URLS.MAP.TRACING.SHORTEST_PATH)}?urn_start=${start}&urn_end=${end}`,
      user_id: sessionStorage.getItem(LoginService.USER_ID_KEY)
    };

    this.mapRequestService.getMapRequest(req).subscribe({
      next: (resp: any) => {
        this.drawTrace(resp.result.features, true);
      },
      error: (err) => {
        console.log('SHORTEST PATH ERROR', err);
        this.showErrorModal('Impossibile trovare un percorso per questi valori');
      }
    });
  }

  drawMapFeature(type: 'start' | 'end') {
    if (type === 'start') {
      this.mapService.addFeatureToTemporaryLayer(
        this.startJSONFeature.geometry,
        this.tracingStartPointStyle,
        true,
        false,
        'trace_start'
      );
    } else {
      this.mapService.addFeatureToTemporaryLayer(
        this.endJSONFeature.geometry,
        this.tracingEndPointStyle,
        true,
        false,
        'trace_end'
      );
    }
  }

  clearMapFeature(type: 'start' | 'end') {
    if (type === 'start') {
      this.mapService.clearFeatureFromTemporaryLayerById('trace_start', true, false);
    } else {
      this.mapService.clearFeatureFromTemporaryLayerById('trace_end', true, false);
    }
  }

  drawTrace(features: any[], mergeFeatures: boolean) {
    const filteredFeatures = features.filter((feature) => feature.geometry.type == 'LineString');

    let linestrings = this.mergeLineStrings(filteredFeatures, mergeFeatures);

    this.mapService.addFeatureToTemporaryLayer(
      this.transformGeoJSONLineGeometry(linestrings, MapService.EPSG_4326_KEY, MapService.EPSG_3857_KEY),
      this.tracingLineStyle,
      true,
      false,
      'trace'
    );
  }

  clearTrace() {
    this.mapService.clearFeatureFromTemporaryLayerById('trace', true, false);
  }

  /**
   * Trasforma le coordinate di una feature GeoJSON LineString o MultiLineString
   * @param {Object} geojsonFeature - Feature GeoJSON valida
   * @param {string} sourceCRS - Proiezione di origine (es. 'EPSG:4326')
   * @param {string} targetCRS - Proiezione di destinazione (es. 'EPSG:3857')
   * @returns {Object} - Nuova geometry GeoJSON con coordinate trasformate
   */
  transformGeoJSONLineGeometry(geojsonFeature: any, sourceCRS: string, targetCRS: string) {
    if (
      !geojsonFeature ||
      geojsonFeature.type !== 'Feature' ||
      !geojsonFeature.geometry ||
      !['LineString', 'MultiLineString'].includes(geojsonFeature.geometry.type)
    ) {
      return this.showErrorModal('La feature deve essere un GeoJSON LineString o MultiLineString valido');
    }

    const geometryType = geojsonFeature.geometry.type;
    const originalCoords = geojsonFeature.geometry.coordinates;

    let transformedCoords;

    if (geometryType === 'LineString') {
      transformedCoords = originalCoords.map((coord) => transform(coord, sourceCRS, targetCRS));
    } else if (geometryType === 'MultiLineString') {
      transformedCoords = originalCoords.map((line) => line.map((coord) => transform(coord, sourceCRS, targetCRS)));
    }

    return {
      type: geometryType,
      coordinates: transformedCoords
    };
  }

  private toKey(coord: [number, number]): string {
    return coord.join(',');
  }

  private prepareSegments(features: any): LineSegment[] {
    return features.map((f) => {
      const coords = f.geometry.coordinates;
      return {
        coords,
        start: this.toKey(coords[0]),
        end: this.toKey(coords[coords.length - 1])
      };
    });
  }

  private orderSegments(segments: LineSegment[]): LineSegment[] {
    const ordered: LineSegment[] = [];
    const used = new Set<number>();

    ordered.push(segments[0]);
    used.add(0);

    while (ordered.length < segments.length) {
      const last = ordered[ordered.length - 1];
      const nextIndex = segments.findIndex((seg, i) => {
        if (used.has(i)) return false;
        return seg.start === last.end || seg.end === last.end;
      });

      if (nextIndex === -1) break;

      let next = segments[nextIndex];
      if (next.end === last.end) {
        next = {
          coords: [...next.coords].reverse() as [number, number],
          start: next.end,
          end: next.start
        };
      }

      ordered.push(next);
      used.add(nextIndex);
    }

    return ordered;
  }

  private mergeLineStringsSegments(segments: LineSegment[]): any {
    const mergedCoords = segments.flatMap((seg, i) => (i === 0 ? seg.coords : seg.coords.slice(1)));

    return {
      type: 'Feature',
      geometry: {
        type: 'LineString',
        coordinates: mergedCoords
      },
      properties: {}
    };
  }

  private mergeMultiLineStrings(features: any): any {
    return {
      type: 'Feature',
      geometry: {
        type: 'MultiLineString',
        coordinates: features.map((el) => el.geometry.coordinates)
      },
      properties: {}
    };
  }

  public mergeLineStrings(features: any, mergeFeatures: boolean): any {
    return this.mergeMultiLineStrings(features);
    // if (mergeFeatures) {
    //   const segments = this.prepareSegments(features);
    //   const ordered = this.orderSegments(segments);
    //   return this.mergeLineStringsSegments(ordered);
    // } else {
    //   return this.mergeMultiLineStrings(features);
    // }
  }
}
